/**
 * Moderation Work Queue Page
 *
 * Displays assigned moderation cases for review and allows moderators to take actions.
 */

import { track } from '@core/logging';
import { srOnly } from '@core/styles/utilities.css';
import {
  ModerationCaseCard,
  ModerationCaseCardHeader,
  ModerationCaseCardSection,
  ModerationTextContent,
  ModerationImageContent,
  ModerationCommentContent,
  ModerationLabelsContent,
  ModerationActionMenu,
} from '@moderation/components/ModerationCaseCard';
import type { ContentTypeInfo } from '@moderation/components/ModerationCaseCard';
import { ModerationClassificationDialog } from '@moderation/components/ModerationClassificationDialog';
import { ModerationLabelsPanel } from '@moderation/components/ModerationLabelsPanel';
import { useAssignModerationCase, useClassificationLookup, useSubmitCaseFeedback } from '@moderation/hooks';
import {
  getAvailableContentTypes,
  getTextMessages,
  getImageItems,
  extractUserComment,
  extractAllMetadata,
  getTextIcon,
  getImageIcon,
  getCommentIcon,
  getLabelsIcon,
} from '@moderation/utils';
import { Button, ButtonType, Heading, LoadingSpinner } from '@soteria/design-system-ve';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as styles from './ModerationWorkQueuePage.css';
import { EmptyQueueState, CompletedState, LoadingState, ErrorState } from './components/ModerationWorkQueueStates';

/* eslint-disable max-statements, complexity */
export function ModerationWorkQueuePage() {
  const navigate = useNavigate();
  const params = useParams<{ communityId: string; topicId: string }>();
  const { communityId, topicId } = params;

  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);

  // Lifted state from ModerationLabelsPanel (controlled component pattern)
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const actionMenuTriggerId = 'moderation-action-menu-trigger';

  const { assignCases, cases, loading, errors } = useAssignModerationCase();
  const { submitFeedback, loading: submitLoading, errors: submitErrors } = useSubmitCaseFeedback();
  const {
    lookupClassification,
    classificationResult,
    isDialogOpen: isClassificationDialogOpen,
    isSearching: isClassificationSearching,
    searchCompleted: classificationSearchCompleted,
    errors: classificationErrors,
    isSafe,
    isContentBlurred,
    closeDialog: closeClassificationDialog,
    toggleBlur: toggleClassificationBlur,
  } = useClassificationLookup();

  // Convert topic ID from URL format to API format (e.g., "chat" -> "Chat")
  const topicName = topicId
    ? topicId
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  const handleReturnToDashboard = useCallback(() => {
    navigate(`/communities/${communityId}/moderation`);
  }, [navigate, communityId]);

  // Fetch cases on mount
  useEffect(() => {
    if (communityId && topicName) {
      void assignCases({
        variables: {
          communityId,
          topicName,
        },
      });
    }
  }, [communityId, topicName, assignCases]);

  // Reset selection state when case changes
  useEffect(() => {
    setSelectedLabels([]);
    setComment('');
  }, [currentCaseIndex]);

  const currentCase = cases[currentCaseIndex];
  const totalCases = cases.length;
  const hasMoreCases = currentCaseIndex < totalCases - 1;

  // Derive Apply button disabled state
  const isApplyDisabled = selectedLabels.length === 0 || submitLoading;

  // Extract data using utilities for new card component
  const contentTypesMap = getAvailableContentTypes(currentCase);
  const textMessages = getTextMessages(currentCase);
  const imageItems = getImageItems(currentCase);
  const userComment = extractUserComment(currentCase);
  const metadata = extractAllMetadata(currentCase);

  // Convert content types Map to ContentTypeInfo array for header
  const contentTypes: ContentTypeInfo[] = useMemo(() => Array.from(contentTypesMap, ([type, count]) => ({ type, count })), [contentTypesMap]);

  // Add alt text to image items
  const imagesWithAlt = useMemo(
    () =>
      imageItems.map((item, index) => ({
        ...item,
        alt: `Image ${index + 1}`,
      })),
    [imageItems]
  );

  // Extract and format labels with IDs
  const labelsWithId = useMemo(() => {
    const firstContent = currentCase?.report?.contents?.[0];
    const allLabels =
      firstContent?.labels?.flatMap(labelGroup =>
        labelGroup.labels.map(l => ({
          name: l.name,
          confidence: l.confidence,
          severity: 'MEDIUM',
        }))
      ) || [];

    return allLabels.map((label, index) => ({
      ...label,
      id: `${label.name}-${label.confidence}-${index}`,
      severity: label.severity.toLowerCase() as 'low' | 'medium' | 'high' | 'critical',
    }));
  }, [currentCase]);

  const handleApplyLabels = async (labelIds: string[], feedbackComment?: string) => {
    if (!currentCase) return;

    try {
      const result = await submitFeedback({
        communityId: currentCase.communityId,
        topicName: currentCase.topicName,
        subjectId: currentCase.subjectId,
        caseId: currentCase.caseId,
        feedbackLabels: labelIds,
        feedbackComment,
      });

      if (result) {
        setCompletedCount(prev => prev + 1);

        // Move to next case or finish
        if (hasMoreCases) {
          setCurrentCaseIndex(prev => prev + 1);
        } else {
          // All cases completed - go back to dashboard
          navigate(`/communities/${communityId}/moderation`);
        }
      }
    } catch (error) {
      track('moderation_feedback_submit_failed', {
        caseId: currentCase?.caseId,
        labelIds: labelIds.join(', '),
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  // Action menu handlers
  const handleActionMenuToggle = useCallback(() => {
    setIsActionMenuOpen(prev => !prev);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setIsActionMenuOpen(false);
  }, []);

  const handleClassification = useCallback(() => {
    if (communityId && currentCase?.subjectId) {
      lookupClassification(communityId, currentCase.subjectId);
    }
  }, [communityId, currentCase?.subjectId, lookupClassification]);

  // Show loading while request is in progress
  if (loading) {
    return <LoadingState />;
  }

  // Show error if there was a problem
  if (errors && errors.length > 0) {
    return <ErrorState message={errors[0].message} onReturn={handleReturnToDashboard} />;
  }

  // Show empty state if no cases were returned
  if (totalCases === 0) {
    return <EmptyQueueState onReturn={handleReturnToDashboard} />;
  }

  // Show completed state if we've gone through all cases
  if (!currentCase && completedCount > 0) {
    return <CompletedState totalCases={completedCount} onReturn={handleReturnToDashboard} />;
  }

  // Shouldn't happen, but handle edge case
  if (!currentCase) {
    return <EmptyQueueState onReturn={handleReturnToDashboard} />;
  }

  return (
    <div className={styles.container}>
      {/* Visually-hidden page title for screen readers */}
      <Heading className={srOnly} level="h1">
        {topicName} Moderation Queue
      </Heading>

      {/* Action Bar — Apply button at top */}
      <div aria-busy={submitLoading} className={styles.actionBar}>
        <Button
          aria-disabled={isApplyDisabled}
          aria-label={`Apply ${selectedLabels.length} selected ${selectedLabels.length === 1 ? 'label' : 'labels'}`}
          buttonType={ButtonType.Primary}
          disabled={isApplyDisabled}
          onClick={() => handleApplyLabels(selectedLabels, comment || undefined)}
        >
          {submitLoading ? <LoadingSpinner /> : 'Apply'}
        </Button>
      </div>

      {/* Two-column grid layout */}
      <div className={styles.contentGrid}>
        <div className={styles.mainContent}>
          {/* Case Information */}
          <ModerationCaseCard>
            <ModerationCaseCardHeader
              showActionMenu
              actionMenuTriggerId={actionMenuTriggerId}
              contentTypes={contentTypes}
              segmentName={metadata.segmentName}
              timestamp={metadata.timestamp}
              username={metadata.username}
              onActionMenuToggle={handleActionMenuToggle}
            />

            {/* Text Content Section */}
            {textMessages.length > 0 && (
              <ModerationCaseCardSection count={textMessages.length} icon={getTextIcon()} title="Text">
                <ModerationTextContent messages={textMessages} />
              </ModerationCaseCardSection>
            )}

            {/* Image Content Section */}
            {imagesWithAlt.length > 0 && (
              <ModerationCaseCardSection count={imagesWithAlt.length} icon={getImageIcon()} title="Image">
                <ModerationImageContent images={imagesWithAlt} />
              </ModerationCaseCardSection>
            )}

            {/* User Comment Section */}
            {userComment && (
              <ModerationCaseCardSection icon={getCommentIcon()} title="Comment">
                <ModerationCommentContent comment={userComment} />
              </ModerationCaseCardSection>
            )}

            {/* Labels Section */}
            <ModerationCaseCardSection icon={getLabelsIcon()} title="Labels">
              <ModerationLabelsContent labels={labelsWithId} />
            </ModerationCaseCardSection>
          </ModerationCaseCard>

          {/* Action Menu (anchored to "..." trigger button) */}
          <ModerationActionMenu
            isOpen={isActionMenuOpen}
            triggerId={actionMenuTriggerId}
            onClassification={handleClassification}
            onClose={handleActionMenuClose}
          />

          {/* Classification Result Dialog (loading → no data → data) */}
          <ModerationClassificationDialog
            classificationResult={classificationResult}
            errors={classificationErrors}
            isContentBlurred={isContentBlurred}
            isOpen={isClassificationDialogOpen}
            isSafe={isSafe}
            isSearching={isClassificationSearching}
            searchCompleted={classificationSearchCompleted}
            onClose={closeClassificationDialog}
            onToggleBlur={toggleClassificationBlur}
          />
        </div>

        {/* Labels Panel */}
        <div className={styles.labelsColumn}>
          <ModerationLabelsPanel
            key={currentCase.caseId}
            caseId={currentCase.caseId}
            comment={comment}
            communityId={currentCase.communityId}
            errors={submitErrors}
            isSubmitting={submitLoading}
            selectedLabels={selectedLabels}
            onCommentChange={setComment}
            onSelectionChange={setSelectedLabels}
          />
        </div>
      </div>
    </div>
  );
}
